/** The global leaderboard: our FastAPI backend (api/solrun.py). Scores that can't be sent are queued and retried when online.
 * Each line is an X handle the player typed (no sign-in — handle.ts); the board links it to the profile on x.com. */

export interface ScoreEntry {
  rank: number;
  /** the X handle, without "@" */
  handle: string;
  score: number;
  distance: number;
  coins: number;
  at: string;
}

export interface ScoreSubmission {
  token: string;
  /** the X handle, without "@" */
  handle: string;
  score: number;
  distance: number;
  coins: number;
  duration_ms: number;
}

export interface SubmitResult {
  rank: number;
  players: number;
  best: boolean;
  /** the handle as the server filed it */
  handle: string;
}

export class Leaderboard {
  private readonly queueKey = "solrun-queue-v1";

  constructor(private base: string) {
    window.addEventListener("online", () => void this.flush());
  }

  get enabled(): boolean {
    return this.base.length > 0;
  }

  /** One token per run, issued before the run starts; the server uses it to check the run's duration. */
  async start(): Promise<string | undefined> {
    try {
      const response = await fetch(`${this.base}/solrun/start`, { method: "POST" });
      if (!response.ok) return undefined;
      return ((await response.json()) as { token: string }).token;
    } catch {
      return undefined;
    }
  }

  /** Posts a score: the result, "queued" (offline or the server is down: retried later), "busy" (rate-limited: retried later)
   *  or "rejected" (the server refused this run — it did not pass the checks). */
  async submit(entry: ScoreSubmission): Promise<SubmitResult | "queued" | "busy" | "rejected" | undefined> {
    let response: Response;
    try {
      response = await fetch(`${this.base}/solrun/scores`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(entry) });
    } catch {
      this.enqueue(entry);
      return "queued";
    }
    if (response.status === 429) { this.enqueue(entry); return "busy"; }
    if (response.status >= 500) { this.enqueue(entry); return "queued"; }
    if (!response.ok) return "rejected";
    try { return (await response.json()) as SubmitResult; } catch { return undefined; }
  }

  async top(limit = 100): Promise<{ entries: ScoreEntry[]; players: number } | undefined> {
    try {
      const response = await fetch(`${this.base}/solrun/scores?limit=${limit}`);
      if (!response.ok) return undefined;
      return (await response.json()) as { entries: ScoreEntry[]; players: number };
    } catch {
      return undefined;
    }
  }

  async flush(): Promise<void> {
    const queue = this.queue();
    if (!queue.length) return;
    localStorage.setItem(this.queueKey, "[]");
    for (const entry of queue) await this.submit(entry);
  }

  private queue(): ScoreSubmission[] {
    try { return JSON.parse(localStorage.getItem(this.queueKey) ?? "[]") as ScoreSubmission[]; } catch { return []; }
  }

  private enqueue(entry: ScoreSubmission): void {
    const queue = this.queue().filter((item) => item.token !== entry.token).slice(-20);
    queue.push(entry);
    try { localStorage.setItem(this.queueKey, JSON.stringify(queue)); } catch { /* storage full or blocked */ }
  }
}
